export const TOTAL_BPS = 10_000;
const WHOLE_PERCENT_BPS = 100;
const FORBIDDEN_SHARE_BPS = 5_000;
const MAX_SHARE_BPS = 9_900;

export const exactPriceCents = (shareBps: number, entryFeeCents: number) =>
  Math.floor((shareBps * entryFeeCents) / TOTAL_BPS);

export const organizerPriceCents = (
  recipientPriceCents: readonly number[],
  entryFeeCents: number
) => Math.max(0, entryFeeCents - recipientPriceCents.reduce((total, cents) => total + cents, 0));

// No valid whole-percent split exists past 99 recipients; the result stays length recipientCount (callers index by position) and fails validateShares.
export const evenRecipientSharesBps = (recipientCount: number) => {
  if (recipientCount < 1) return [];
  if (recipientCount === 1) return [FORBIDDEN_SHARE_BPS - WHOLE_PERCENT_BPS];
  const participantCount = recipientCount + 1;
  const baseShare =
    Math.floor(TOTAL_BPS / participantCount / WHOLE_PERCENT_BPS) * WHOLE_PERCENT_BPS;
  let remainder = (TOTAL_BPS - baseShare * participantCount) / WHOLE_PERCENT_BPS;
  remainder = Math.max(0, remainder - 1);
  return Array.from({ length: recipientCount }, () => {
    const extra = remainder > 0 ? WHOLE_PERCENT_BPS : 0;
    if (extra > 0) remainder -= 1;
    return baseShare + extra;
  });
};

export const organizerShareBps = (recipientShares: readonly number[]) =>
  TOTAL_BPS - recipientShares.reduce((total, share) => total + share, 0);

export type ShareValidationError =
  | 'FIFTY_PERCENT_NOT_ALLOWED'
  | 'SUM_EXCEEDS_TOTAL'
  | 'SHARE_OUT_OF_RANGE'
  | 'SHARE_NOT_WHOLE_PERCENT'
  | 'NO_RECIPIENTS';

export const validateShares = (recipientShares: readonly number[]) => {
  const rowErrors = recipientShares.map<ShareValidationError | null>((share) => {
    if (share === FORBIDDEN_SHARE_BPS) return 'FIFTY_PERCENT_NOT_ALLOWED';
    if (share % WHOLE_PERCENT_BPS !== 0) return 'SHARE_NOT_WHOLE_PERCENT';
    if (share < WHOLE_PERCENT_BPS || share > MAX_SHARE_BPS) return 'SHARE_OUT_OF_RANGE';
    return null;
  });
  const organizerBps = organizerShareBps(recipientShares);
  const organizerError: ShareValidationError | null =
    organizerBps === FORBIDDEN_SHARE_BPS ? 'FIFTY_PERCENT_NOT_ALLOWED' : null;
  // With empty splits rejected, every row is >=100 bps, so an organizer MAX_SHARE_BPS ceiling would be unreachable.
  let summaryError: ShareValidationError | null = null;
  if (recipientShares.length === 0) summaryError = 'NO_RECIPIENTS';
  else if (organizerBps < 0) summaryError = 'SUM_EXCEEDS_TOTAL';
  else if (organizerBps === 0) summaryError = 'SHARE_OUT_OF_RANGE';

  return {
    isValid:
      rowErrors.every((error) => error === null) &&
      organizerError === null &&
      summaryError === null,
    rowErrors,
    organizerError,
    summaryError,
    organizerBps,
  };
};

/**
 * Mirrors `MAX_ENTRY_OWNERS` / `MAX_INVITES_PER_BATCH` in marketplace's
 * create-split-invites.dto.ts. The organizer holds one of the ten owner slots,
 * so a batch can only fill the nine that remain, and the wire contract caps
 * `invites` at `maxItems: 9`. Selecting past this is a 400 the panel cannot
 * anticipate, so the ceiling is enforced here and shown to the organizer.
 *
 * This is the batch ceiling only. The per-entry cap counts existing
 * shareholders and outstanding invites too, and is enforced server-side in the
 * creation transaction — an entry that already has owners admits fewer than
 * nine, and that shortfall still surfaces as `SPLIT_OWNER_LIMIT_EXCEEDED`.
 */
export const MAX_ENTRY_OWNERS = 10;
export const MAX_INVITES = MAX_ENTRY_OWNERS - 1;
export const bpsToPercent = (bps: number) => bps / 100;
export const percentToBps = (percent: number) => Math.round(percent * 100);
